import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import {
  changeSubscription,
  createSubscription,
  getPlan,
  getPlanConfig,
  getVoucherByCode,
} from "../../../services/plan"
import intl from "react-intl-universal"

const PlanContext = createContext(null)

export const usePlanContext = () => useContext(PlanContext)

export function PlanProvider({ children }) {
  const [bIsLoading, setIsLoading] = useState(true)

  /* *********** Dados padrão do sistema *********** */
  const [oSystemConfig, setSystemConfig] = useState({})
  const [aModules, setModules] = useState([])

  /* *********** Dados do plano *********** */
  const [oCustomer, setCustomer] = useState({})
  const [oVoucher, setVoucher] = useState({})

  const [bIsAnnual, setIsAnnual] = useState(false)

  const [oHiredModulesOld, setHiredModulesOld] = useState({})
  const [aHiredModules, setHiredModules] = useState([])

  // prettier-ignore
  const addModule = useCallback((nModId) => {
    if (aHiredModules.includes(nModId)) return
    setHiredModules((aPrv) => [...aPrv, nModId])
  }, [aHiredModules])

  const removeModule = useCallback(
    (nModId) => setHiredModules((aPrv) => aPrv.filter((nId) => nId !== nModId)),
    [aHiredModules],
  )

  const [oHiredUsersOld, setHiredUsersOld] = useState({})
  const [nHiredUsers, setHiredUsers] = useState(0)

  const [oHiredSpaceOld, setHiredSpaceOld] = useState({})
  const [nHiredSpace, setHiredSpace] = useState(0)

  const nDiffUsers = useMemo(
    () => nHiredUsers - oHiredUsersOld.prod_namount,
    [nHiredUsers, oHiredUsersOld],
  )

  const nDiffSpace = useMemo(
    () => nHiredSpace - oHiredSpaceOld.prod_namount,
    [nHiredSpace, oHiredSpaceOld],
  )

  const aDiffModules = useMemo(() => {
    const aDiff = []
    for (const oMod of aModules) {
      const bInNew = aHiredModules.includes(oMod.mod_nid)
      const bInOld = !!oHiredModulesOld[oMod.mod_nid]
      if (bInOld !== bInNew) {
        aDiff.push({ ...oMod, bAdded: bInNew })
      }
    }
    return aDiff
  }, [aModules, aHiredModules, oHiredModulesOld])

  const calcProductPrice = useCallback(
    (nBaseUnitPrice, nAmount = 1, oVoucher = null, oProductOld = {}) => {
      const nOldAmount = oProductOld.prod_namount ?? 0
      /** @type {number} */
      const nOldTotalPrice = oProductOld.prod_nprice ?? 0

      if (nOldAmount === nAmount) return nOldTotalPrice
      if (nOldAmount > nAmount) {
        // reduz pelo "preço unitário médio" dos produtos já contratados
        const nPrice = Math.ceil(nOldTotalPrice / nOldAmount)
        return nPrice * nAmount
      }

      // produtos novos, pagar preço de acordo com plano atual
      const nVchDiscount = oVoucher.vch_ndiscount ?? 0
      const nUnitPrice = Math.ceil((nBaseUnitPrice * (100 - nVchDiscount)) / 100)

      const nDiffAmount = nAmount - nOldAmount
      return nOldTotalPrice + nUnitPrice * nDiffAmount
    },
    [],
  )

  const nPriceMonth = useMemo(() => {
    if (bIsLoading) return 0
    // se não selecionar nenhum módulo, plano não é válido: manter valor zero
    if (aHiredModules.length === 0) return 0

    // em centavos de real: 1298 -> R$12,98
    let nNewPrice = oSystemConfig.software_basic_value ?? 0
    const nVchDiscount = oVoucher.vch_ndiscount ?? 0
    const nVchPricePct = (100 - nVchDiscount) / 100

    // módulos
    for (const nModId of aHiredModules) {
      if (oHiredModulesOld[nModId]) {
        // módulo já contratado, mantém o preço/descontos já existentes
        nNewPrice += oHiredModulesOld[nModId].prod_nprice
      } else {
        const nModPrice = aModules.find((oMod) => oMod.mod_nid === nModId).mod_nvalue
        nNewPrice += nModPrice * nVchPricePct
      }
    }

    let nUsersPrice = calcProductPrice(
      oSystemConfig.value_per_user,
      nHiredUsers,
      oVoucher,
      oHiredUsersOld,
    )
    console.log("users price", nUsersPrice)
    nNewPrice += nUsersPrice

    let nSpacePrice = calcProductPrice(
      oSystemConfig.value_per_gigabyte,
      nHiredSpace,
      oVoucher,
      oHiredSpaceOld,
    )
    console.log("space price", nSpacePrice)
    nNewPrice += nSpacePrice

    nNewPrice = Math.ceil(nNewPrice)

    return nNewPrice
  }, [
    aHiredModules,
    bIsLoading,
    nDiffSpace,
    nDiffUsers,
    nHiredSpace,
    nHiredUsers,
    oHiredModulesOld,
    oHiredSpaceOld,
    oHiredUsersOld,
    oSystemConfig,
    oVoucher,
  ])

  const nPriceYear = useMemo(() => {
    if (nPriceMonth === 0) return 0
    let nNewPrice = nPriceMonth * 12
    nNewPrice *= (100 - oSystemConfig.discount_annual_plan) / 100
    nNewPrice = Math.ceil(nNewPrice)
    return nNewPrice
  }, [oSystemConfig, nPriceMonth])

  /* ********* Funções ********* */

  const validateVoucher = async (cCode) => {
    if (!cCode) return
    try {
      const oResult = await getVoucherByCode(cCode)
      setVoucher(oResult)
    } catch (oError) {
      setVoucher({})
      if (oError.response?.status === 400) {
        throw oError.response.data
      }
    }
  }

  const clearVoucher = () => {
    setVoucher({})
  }

  const prepareToSubmit = useCallback(
    (oFormData) => {
      if (aHiredModules.length === 0) {
        throw { response: { data: intl.get("plan.errors.no_modules_selected") } }
      }
      const { ca_bissimple, ca_bispublic } = oFormData
      delete oFormData.ca_bissimple
      delete oFormData.ca_bispublic

      if (ca_bissimple) {
        oFormData.cust_etaxframework = "Optante"
      } else if (ca_bispublic) {
        oFormData.cust_etaxframework = "Isento"
      } else {
        oFormData.cust_etaxframework = "Não optante"
      }

      Object.assign(oFormData, {
        cust_etaxframework: ca_bissimple
          ? "Optante"
          : ca_bispublic
          ? "Isento"
          : "Não optante",
        cust_nplanid: oFormData.sig_bisannual
          ? oSystemConfig.plan_nid_annual
          : oSystemConfig.plan_nid_monthly,

        cust_ntotalusers: nHiredUsers,
        cust_ntotalspace: nHiredSpace,
        sig_amodulesnid: aHiredModules,

        sig_npayamount: oFormData.sig_bisannual ? nPriceYear : nPriceMonth,
        vch_ndiscount: oVoucher.vch_nid ? Number(oVoucher.vch_ndiscount) : null,
        vch_nid: oVoucher.vch_nid ?? null,
      })

      return oFormData
    },
    [oSystemConfig, oVoucher, nHiredUsers, nHiredSpace, aHiredModules],
  )

  const submitPlan = async (oFormData) => {
    prepareToSubmit(oFormData)
    const { cAuthenticatedLink } = oCustomer.bIsTrial
      ? await createSubscription(oFormData)
      : await changeSubscription(oFormData)
    setTimeout(() => loadInitialData(), 500)
    return cAuthenticatedLink
  }

  const loadConfig = useCallback(async () => {
    const oResult = await getPlanConfig()

    setCustomer((oPrv) => ({ ...oPrv, ...oResult.oCustomer }))
    const oSysConfig = oResult.aConfigs.reduce((oConf, oItem) => {
      oConf[oItem.sysc_cid] = Number(oItem.sysc_cvalue)
      return oConf
    }, {})
    setSystemConfig(oSysConfig)
    setModules(oResult.aModules)
    setIsAnnual(oResult.oCustomer.cust_mplanid === oSysConfig.plan_nid_annual)
    setHiredSpace((nSpace) => Math.max(nSpace, oSysConfig.min_space))
    setHiredUsers((nUsers) => Math.max(nUsers, oSysConfig.default_total_users))
  }, [])

  const loadPlan = useCallback(async () => {
    const oResult = await getPlan()

    setCustomer((oPrv) => ({ ...oPrv, ...oResult.oCustomer }))

    const oHiredModules = oResult.oPlan
      .filter((oItem) => oItem.prod_ctype === "module")
      .reduce((oModules, oItem) => {
        oModules[oItem.mod_nid] = {
          mod_nid: oItem.mod_nid,
          prod_nprice: oItem.prod_nprice,
          vch_nid: oItem.vch_nid,
          vch_ndiscount: oItem.vch_ndiscount,
        }
        return oModules
      }, {})
    setHiredModulesOld(oHiredModules)
    setHiredModules(Object.keys(oHiredModules).map(Number))

    const oEmpty = { prod_namount: 0 }

    // caso já contratou mas ainda não pagou, considerar valores contratados
    const bEmptyModules = Object.keys(oHiredModules).length === 0
    const { bIsTrial, nUserCount } = oResult.oCustomer

    const oUsers = oResult.oPlan.find((oItem) => oItem.prod_ctype === "user") ?? oEmpty
    setHiredUsersOld(bIsTrial && bEmptyModules ? oEmpty : oUsers)
    setHiredUsers((nUsers) => Math.max(nUsers, oUsers.prod_namount, nUserCount))

    const oSpace = oResult.oPlan.find((oItem) => oItem.prod_ctype === "space") ?? oEmpty
    setHiredSpaceOld(bIsTrial && bEmptyModules ? oEmpty : oSpace)
    setHiredSpace((nSpace) => Math.max(nSpace, oSpace.prod_namount))
  }, [])

  const loadInitialData = useCallback(async () => {
    try {
      await Promise.all([loadConfig(), loadPlan()])
    } catch (oError) {
      console.log("error", oError)
    }
    setIsLoading(false)
  }, [])

  useEffect(() => {
    loadInitialData()
  }, [])

  const oCtxValue = useMemo(() => ({
    bIsLoading,

    oSystemConfig,
    aModules,
    oCustomer,
    bIsAnnual,

    oVoucher,

    oHiredModulesOld,
    aHiredModules,
    addModule,
    removeModule,

    oHiredUsersOld,
    nHiredUsers,
    setHiredUsers,

    oHiredSpaceOld,
    nHiredSpace,
    setHiredSpace,

    nDiffSpace,
    nDiffUsers,
    aDiffModules,

    nPriceMonth,
    nPriceYear,
    submitPlan,
    changeSubscription,
    validateVoucher,
    clearVoucher,
  }))
  return <PlanContext.Provider value={oCtxValue}>{children}</PlanContext.Provider>
}
